import React, { useState, useEffect, useRef, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import _ from "lodash";
import cx from "classnames";

import { Button } from "react-bootstrap";

import dateformat from "dateformat";
import { convertDatesToTimezone } from "services/report.service";
import { TableFooterInfo, TableHeaderInfo } from "..";
import { EmptyMessage, LoadingMessage } from "../../reports/components";
import { storyboardReportsHeader } from "constants/workspaceReportsData";
import { fetchingWorkspaceStoryboardReports, fetchStoryboardReportsForDownload, invalidateStoryboardReportDownloadData } from "actions/workspaceReports";
import { StoryboardDetails } from ".";
import { CSVLink } from "react-csv";
import { formatDurationWithLabel } from "../../reports/reporthelpers";

import styles from '../workspaceReports.module.scss';

export default function WorkspaceStoryboardReports({ date, contentType, modalView = false, sectionId = "" }) {
  const dispatch = useDispatch();

  const [currentChart, setCurrentChart] = useState("table_view");
  const loading = useSelector(state => _.get(state, `workspaces.reports.storyboardReports.loading`, false));
  const data = useSelector(state => _.get(state, `workspaces.reports.storyboardReports.items`, []));
  const pagination = useSelector(state => _.get(state, 'workspaces.reports.storyboardReports.pagination', {}));
  const totalCount = pagination.totalRecordsCount || 0;

  useEffect(() => {
    if (date || (contentType && contentType.length > 0)) {
      handlePageChange(0);
    }
  }, [date, contentType]);

  const handlePageChange = (page) => {
    handleStoryboardReports(page);
  }

  const handleStoryboardReports = (page) => {
    if (pagination.current != -1) {
      const request = {
        types: contentType,
        ...convertDatesToTimezone(date),
      }

      dispatch(fetchingWorkspaceStoryboardReports(request, page, 20));
    }
  }

  useEffect(() => {
    if (pagination.current == -1) {
      const request = {
        types: contentType,
        ...convertDatesToTimezone(date),
      }
      dispatch(fetchingWorkspaceStoryboardReports(request, 0, 20));
    }
  }, [pagination]);

  return (
    <div className={cx(styles.storyboardReportsWrapper, { [styles.modalWrapper]: modalView })} id={sectionId}>
      <div className={cx(styles.storyboardReportsContainer, { [styles.reportsContainer]: modalView})}>
        <TableHeaderInfo title="Best Performing Storyboards" count={pagination.totalRecordsCount || 0} modalView={modalView} />
        <>
          <StoryboardReportsView
            data={data}
            modalView={modalView}
            count={totalCount}
            date={date}
            type={contentType}
            loading={loading}
            contentType={contentType}
          />
        </>
        {/* {currentChart == "graph_view" &&
                        <div className={styles.graphView}>Graph view</div>
                    } */}
      </div>
      <TableFooterInfo
          data={data}
          currentChart={currentChart}
          disableView={['pie_chart_view']}
          modalView={modalView}
          onChange={handlePageChange}
          type='storyboardReports'
          pagination={pagination}
          paginationName={"Storyboards"}
          date={date}
          contentType={contentType}
        />
    </div>
  )
}

export const StoryboardReportsView = ({ modalView, data, type, date, loading }) => {

  const reportsData = useSelector(state => {
    const records = Array.isArray(data) ? data : [];
    return modalView ? records : records.slice(0, 5);
  });

  return (
    <>
      <div className={cx(styles.storyboardReportsView, { [styles.tableDataOverflow]: modalView })}>
        <div className={cx(styles.tableHeaderWrapper, { [styles.sticky]: modalView })}>
          <div className={styles.downloadOption}>
            <ExportCSV date={date} type={type} />
          </div>
        </div>

        <table className={cx(styles.storyboardReportsTable)}>
          <thead className={cx(styles.tableHeader, { [styles.stickyHeader]: modalView })}>
            <tr className={styles.headerRow}>
              {storyboardReportsHeader.map((column, key) => {
                return <th key={key} className={styles.headerData}>{column}</th>
              })}
            </tr>
          </thead>

          {loading &&
            <div className={cx(styles.loader, { [styles.modalLoader]: modalView })}>
              <LoadingMessage className={styles.loadingMessage} />
            </div>
          }
          {!loading && data.length == 0 &&
            <div className={cx(styles.loader, { [styles.modalLoader]: modalView })}>
              <EmptyMessage />
            </div>
          }
          {!loading && data && data.length > 0 &&
            <tbody className={styles.tableBody}>
              {reportsData.map((data) => (
                <>
                  <tr className={styles.tableBodyDataRow}>
                    <td className={styles.tableBodyData}>
                      <StoryboardDetails data={data} />
                    </td>
                    <td className={styles.tableBodyData}>
                      <p className={styles.views}>{data.views}</p>
                    </td>
                    <td className={styles.tableBodyData}>
                      <p className={styles.leads}>{data.leads}</p>
                    </td>
                    <td className={styles.tableBodyData}>
                      <p className={styles.visitors}>{data.visitors}</p>
                    </td>
                    <td className={styles.tableBodyData}>
                      <p className={styles.timeSpent}>{formatDurationWithLabel(data.timeSpent).value}</p>
                    </td>
                  </tr>
                </>
              ))}
            </tbody>
          }
        </table>
      </div>
    </>
  )
}

function ExportCSV({ date, type }) {
  const dispatch = useDispatch();

  const downloads = useSelector(state => _.get(state, 'workspaces.download.storyboardReports.items', []));
  const completed = useSelector(state => _.get(state, 'workspaces.download.storyboardReports.completed'));
  const progress = useSelector(state => _.get(state, 'workspaces.download.storyboardReports.progress'));
  const pagination = useSelector(state => _.get(state, 'workspaces.reports.storyboardReports.pagination', {}));
  const [csvData, setCsvData] = useState([]);
  const csvLink = useRef();

  const headers = [
    { label: "Storyboard Name", key: "storyboardName" },
    { label: "Storyboard Type", key: "contentType" },
    { label: "Added Date", key: "addedDate" },
    { label: "Storyboard Last Published", key: "updatedDate" },
    { label: "Views", key: "views" },
    { label: "Visitors", key: "visitors" },
    { label: "Leads", key: "leads" },
    { label: "Total Time", key: "totalTime" },
  ];

  useEffect(() => {
    if (completed) {
      var columns = [];
      downloads && downloads.map((data, index) => {

        columns[index] = {
          storyboardName: data.storyboardResponse.name,
          contentType: data.storyboardResponse.type,
          addedDate: data.storyboardResponse.createdDate == undefined ? "" : dateformat(data.storyboardResponse.createdDate, 'dd mmm yyyy'),
          updatedDate: data.lastPublished == undefined ? "" : dateformat(data.lastPublished, 'dd mmm yyyy'),
          views: data.views,
          visitors: data.visitors,
          leads: data.leads,
          totalTime: formatDurationWithLabel(data.timeSpent).value,
        }
      });

      setCsvData(columns);
    }
  }, [completed]);

  useEffect(() => {
    if (!completed && csvData && csvData.length == 0) {
      return;
    }

    csvLink.current.link.click();
    dispatch(invalidateStoryboardReportDownloadData());
  }, [csvData]);

  const fetchStoryboardReportData = () => {
    dispatch(invalidateStoryboardReportDownloadData());

    const request = {
      ...convertDatesToTimezone(date),
      types: type
    }
    dispatch(fetchStoryboardReportsForDownload(request, 0, 200));
  }

  return (
    <div>
      <Button variant="transparent" disabled={progress} onClick={() => fetchStoryboardReportData()} className={styles.download}>
        <i className={cx("cleverstory-download", styles.downloadIcon)} />
        <span className={styles.progress}>{progress ? "Downloading..." : "Download "}</span>
      </Button>
      <CSVLink
        ref={csvLink}
        headers={headers}
        data={csvData}
        filename='bestPerformingStoryboardReports.csv'
        className='hidden'
      />
    </div>
  )
}